import React, { useMemo, useState } from "react";
import { Card, CardBody } from "reactstrap";
import CardHeader from "../Layout/CardHeader";
import SuperInsightsModal from "../Ia/SuperInsightsModal";
import Meta from '../Layout/Meta';
import { ResponsiveContainer, PieChart as RPieChart, Pie as RPie, Cell, Tooltip as RTooltip, Legend as RLegend } from "recharts";

const PieChart = ({ item }) => {
  const safeItem = item || {};

  const {
    Sigla = '',
    Descricao_Dashboard_Componente: titulo = "-",
    dadosComponente,
    Valor_Meta = {},
    Texto_Info = "-",
    Texto_BizAI = "",
    ESG_Info = "-",
    Codigo_Transacao_Link = "",
    Titulo_Transacao_Link = ""
  } = safeItem;

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const togglePopup = () => setIsPopupOpen(!isPopupOpen);
  const slugId = Sigla.trim().replace(/\s+/g, "-");

  // Pré-validação: se vier labels vazias e/ou todos valores 0, não renderiza o gráfico
  const parsedInput = useMemo(() => {
    try {
      const raw = safeItem?.dadosComponente;
      return typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
    } catch (_) { return {}; }
  }, [safeItem?.dadosComponente]);
  const preHasData = useMemo(() => {
    const labels = Array.isArray(parsedInput?.labels) ? parsedInput.labels : [];
    const ds0 = Array.isArray(parsedInput?.datasets) && parsedInput.datasets[0] ? parsedInput.datasets[0] : {};
    const data = Array.isArray(ds0?.data) ? ds0.data.map(v => Number(v || 0)) : [];
    const lblsOk = labels.filter(l => String(l || '').trim().length > 0).length > 0;
    const anyValue = data.some(v => v !== 0);
    return lblsOk && anyValue;
  }, [parsedInput]);

  // Configs visuais
  const FONT_FAMILY = "Poppins, sans-serif";
  const DEFAULT_COLORS = ["#72C3FF", "#1E2A78", "#3D8EF8", "#4DB6AC", "#FFA726", "#E57373"]; 

  const chartData = useMemo(() => {
    try {
      const raw = safeItem?.dadosComponente;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
      const labels = Array.isArray(parsed?.labels) ? parsed.labels : [];
      const valueStrings = Array.isArray(parsed?.value) ? parsed.value : [];
      const ds0 = Array.isArray(parsed?.datasets) && parsed.datasets[0] ? parsed.datasets[0] : {};
      const data = Array.isArray(ds0?.data) ? ds0.data.map((v) => Number(v || 0)) : [];
      const bg = Array.isArray(ds0?.backgroundColor) ? ds0.backgroundColor : DEFAULT_COLORS;
      const total = data.reduce((a, b) => a + (Number(b) || 0), 0);
      // Se o array de data já está em percentuais (soma aproximadamente 100), usa como percentage
      const sum = data.reduce((a, b) => a + b, 0);
      const looksLikePercentage = sum > 99.5 && sum < 100.5;
      const percentage = looksLikePercentage
        ? data
        : data.map((v) => total > 0 ? (Number(v || 0) * 100) / total : 0);
      const items = labels.map((label, idx) => ({
        name: String(label || ''),
        value: Number(data[idx] || 0),
        percentage: Number(percentage[idx] || 0),
        valueStr: valueStrings[idx] || '',
        color: bg[idx % bg.length]
      }));
      return { items, total };
    } catch (_) {
      return { items: [], total: 0 };
    }
  }, [safeItem?.dadosComponente]);

  const hasData = useMemo(() => {
    const items = Array.isArray(chartData?.items) ? chartData.items : [];
    const anyValue = items.some(it => Number(it.value || 0) !== 0);
    return items.length > 0 && anyValue;
  }, [chartData]);

  const dataForPie = chartData.items || [];

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const entry = payload[0]?.payload || {};
    const pct = Number(entry.percentage || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const label = String(entry.name || '');
    const valueStr = entry.valueStr;
    return (
      <div style={{ background: '#FFF', border: '1px solid #ccc', borderRadius: 5, padding: 10, color: '#000', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}>
        <div style={{ fontFamily: FONT_FAMILY, fontSize: 12, fontWeight: 700, color: '#666', marginBottom: 6 }}>{label}</div>
        <div style={{ fontFamily: FONT_FAMILY, fontSize: 11, fontWeight: 600 }}>{valueStr ? valueStr : pct + '%'}
        </div>
      </div>
    );
  };


  return (
    <>
      <Card className="card-dash-item w-100 h-100">    
        <CardBody className="d-flex flex-column">      
          <CardHeader
            titulo={titulo}
            Texto_Info={Texto_Info}
            ESG_Info={ESG_Info}
            togglePopup={togglePopup}
            slugId={slugId}
            textoIA={Texto_BizAI}
            Codigo_Transacao_Link={Codigo_Transacao_Link}
            Titulo_Transacao_Link={Titulo_Transacao_Link}
          />
          <Meta Valor_Meta={Valor_Meta} />
        
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ marginTop: "6px", height: "360px", minHeight: "360px", width: "100%" }}
          >
            {(preHasData && hasData) ? (
              <ResponsiveContainer>
                <RPieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <RPie
                    data={dataForPie}
                    dataKey="value"
                    nameKey="name"
                    outerRadius="90%"
                    startAngle={90}
                    endAngle={-270}
                    isAnimationActive={false}
                    stroke="none"
                  >
                    {dataForPie.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                    ))}
                  </RPie>
                  <RTooltip content={<CustomTooltip />} />
                  <RLegend
                    iconType="circle"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ fontFamily: FONT_FAMILY, fontSize: 12, color: "#000" }}
                    formatter={(value) => (
                      <span style={{ color: "#000", fontSize: 12, fontFamily: FONT_FAMILY, fontWeight: 400 }}>{value}</span>
                    )}
                  />
                </RPieChart>
              </ResponsiveContainer>
            ) : (
              <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 14 }}>
                Sem dados para exibir
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <SuperInsightsModal
        isOpen={isPopupOpen}
        toggle={togglePopup}
        textoIA={Texto_BizAI}
      />
    </>
  );
};

export default PieChart;
